import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { achievementById, claimableTiers, isSecretRarity, type BoosterType, type UnlockKind } from '@anidex/shared/economy';
import type {
  AchievementClaimDTO,
  AchievementDTO,
  AchievementsDTO,
  BoosterOpeningSummaryDTO,
  ClaimLoginCalendarResultDTO,
  ClaimQuestResultDTO,
  GachaStateDTO,
  HarvestDTO,
  LoginCalendarDTO,
  OpenBoosterResultDTO,
  OwnedCharacterIdsDTO,
  OwnedUnlockKeysDTO,
  Page,
  QuestsDTO,
  UnlockDTO,
  UnlockGroupsDTO,
} from '@anidex/shared/types';
import { api, queryString } from '../api';
import type { SessionPayload } from './account';
import { invalidateKeys, refreshProgress, updateSessionBalance } from './cache';
import { keys } from './keys';
import { cursorPagination } from './pagination';

export function useGacha() {
  return useQuery({ queryKey: keys.gacha, queryFn: () => api<GachaStateDTO>('/gacha') });
}

export function useGachaHistory() {
  return useInfiniteQuery({
    queryKey: keys.history,
    queryFn: ({ pageParam }) => api<Page<BoosterOpeningSummaryDTO>>(`/gacha/history${queryString({ cursor: pageParam })}`),
    ...cursorPagination,
  });
}

export function useQuests() {
  return useQuery({ queryKey: keys.quests, queryFn: () => api<QuestsDTO>('/quests') });
}

export function useHarvest() {
  return useQuery({ queryKey: keys.harvest, queryFn: () => api<HarvestDTO>('/harvest') });
}

export function useLoginCalendar() {
  return useQuery({ queryKey: keys.loginCalendar, queryFn: () => api<LoginCalendarDTO>('/login-calendar') });
}

export function useClaimLoginCalendar() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: { characterId?: number } = {}) =>
      api<ClaimLoginCalendarResultDTO>('/login-calendar/claim', { body: input }),
    onSuccess: async (result) => {
      
      await client.cancelQueries({ queryKey: keys.loginCalendar, exact: true });
      client.setQueryData(keys.loginCalendar, result.calendar);
      refreshProgress(client, result.balance, { loginCalendar: false });
    },
  });
}

export function useAchievements(enabled = true) {
  return useQuery({
    queryKey: keys.achievements,
    queryFn: () => api<AchievementsDTO>('/achievements'),
    enabled,
    
    staleTime: 5 * 60_000,
  });
}

function unlocksPath(owner: string | null): string {
  return owner ? `/users/${encodeURIComponent(owner)}/unlocks` : '/me/unlocks';
}


export function useUnlockGroups(owner: string | null, enabled: boolean) {
  return useQuery({
    queryKey: keys.unlocks(owner ?? 'me'),
    queryFn: () => api<UnlockGroupsDTO>(unlocksPath(owner)),
    enabled,
  });
}


export function useUnlockKind(owner: string | null, kind: UnlockKind, firstPage: Page<UnlockDTO>) {
  return useInfiniteQuery({
    queryKey: keys.unlockKind(owner ?? 'me', kind),
    queryFn: ({ pageParam }) => api<Page<UnlockDTO>>(`${unlocksPath(owner)}/${kind}${queryString({ cursor: pageParam })}`),
    ...cursorPagination,
    initialData: { pages: [firstPage], pageParams: [undefined] },
    staleTime: Number.POSITIVE_INFINITY,
  });
}


export function useOwnedUnlocks(kind: UnlockKind, enabled = true) {
  return useQuery({
    queryKey: keys.ownedUnlocks(kind),
    queryFn: () => api<OwnedUnlockKeysDTO>(`/me/unlocks/${kind}/owned`),
    enabled,
  });
}

export interface OpenBoosterOutcome extends OpenBoosterResultDTO {
  
  reachedAchievements: AchievementDTO[];
}


function newlyReached(previous: AchievementsDTO | undefined, next: AchievementDTO[]): AchievementDTO[] {
  const before = new Map(previous?.achievements.map((achievement) => [achievement.id, achievement.claimable]));
  return next.filter((achievement) => achievement.claimable > (before.get(achievement.id) ?? 0));
}

export function useOpenBooster() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (input: { boosterType: BoosterType; quantity: number; idempotencyKey: string }): Promise<OpenBoosterOutcome> => {
      const result = await api<OpenBoosterResultDTO>('/gacha/open', {
        body: { boosterType: input.boosterType, quantity: input.quantity },
        headers: { 'Idempotency-Key': input.idempotencyKey },
      });
      const reachedAchievements = result.achievements
        ? newlyReached(client.getQueryData<AchievementsDTO>(keys.achievements), result.achievements)
        : [];
      return { ...result, reachedAchievements };
    },
    onSuccess: (result) => {
      
      if (result.achievements) client.setQueryData<AchievementsDTO>(keys.achievements, { achievements: result.achievements });
      refreshProgress(client, result.balance, { achievements: !result.achievements });
      
      client.setQueryData<OwnedCharacterIdsDTO>(keys.ownedIds, (old) =>
        old ? { ids: [...new Set([...old.ids, ...result.cards.map((card) => card.character.id)])] } : old,
      );
      if (result.cards.some((card) => isSecretRarity(card.character.rarity))) {
        client.setQueryData<SessionPayload>(keys.session, (old) =>
          old?.user ? { ...old, user: { ...old.user, senitaUnlocked: true } } : old,
        );
      }
    },
  });
}

export function useClaimQuest() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: { questId: string; characterId?: number }) =>
      api<ClaimQuestResultDTO>(`/quests/${input.questId}/claim`, { body: { characterId: input.characterId } }),
    onSuccess: (result) => refreshProgress(client, result.balance),
  });
}

function withProgress(achievement: AchievementDTO, progress: number): AchievementDTO {
  const def = achievementById(achievement.id);
  return def ? { ...achievement, progress, claimable: claimableTiers(def, achievement.tier, progress) } : achievement;
}


export function useClaimAchievement() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (achievementId: string) =>
      api<AchievementClaimDTO>(`/achievements/${encodeURIComponent(achievementId)}/claim`, { method: 'POST', body: {} }),
    onSuccess: (result) => {
      const level = Math.max(0, ...result.rewards.levelUps.map((levelUp) => levelUp.level));
      client.setQueryData<AchievementsDTO>(keys.achievements, (old) =>
        old
          ? {
              achievements: old.achievements.map((achievement) => {
                if (achievement.id === result.achievement.id) return result.achievement;
                
                if (achievement.id === 'anima_balance') return withProgress(achievement, result.balance);
                if (achievement.id === 'player_level' && level > achievement.progress) return withProgress(achievement, level);
                return achievement;
              }),
            }
          : old,
      );
      updateSessionBalance(client, result.balance);
      invalidateKeys(client, [keys.session, keys.me, keys.gacha, keys.quests]);
      if (result.reward.unlocks?.length) void client.invalidateQueries({ queryKey: ['unlocks'] });
    },
  });
}

export function useRerollQuest() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (questId: string) => api<QuestsDTO>(`/quests/${questId}/reroll`, { method: 'POST', body: {} }),
    onSuccess: (board) => {
      client.setQueryData(keys.quests, board);
    },
  });
}
